import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import authApi, { Admin, LoginRequest } from '@/services/api/authApi';

interface AuthState {
  isAuthenticated: boolean;
  admin: Admin | null;
  token: string | null;
  loading: boolean;
  checkingAuth: boolean;
  error: string | null;
}

const initialState: AuthState = {
  isAuthenticated: false,
  admin: null,
  token: localStorage.getItem('accessToken'),
  loading: false,
  checkingAuth: true,
  error: null,
};

// Async Thunks
export const loginAsync = createAsyncThunk('auth/loginAsync', async (loginData: LoginRequest, { rejectWithValue }) => {
  try {
    console.log('[AuthSlice] ========== LOGIN START ==========');
    console.log('[AuthSlice] Calling authApi.login...');

    const response = await authApi.login(loginData);

    console.log('[AuthSlice] ========== LOGIN RESPONSE ==========');
    console.log('[AuthSlice] Full response:', response);
    console.log('[AuthSlice] response.success:', response.success);
    console.log('[AuthSlice] response.data:', response.data);

    if (response.success && response.data) {
      const { tokens, user } = response.data;

      console.log('[AuthSlice] ========== EXTRACTED DATA ==========');
      console.log('[AuthSlice] tokens:', tokens);
      console.log('[AuthSlice] user:', user);

      // Validate tokens exist
      if (!tokens) {
        console.error('[AuthSlice] ERROR: tokens is undefined or null!');
        return rejectWithValue('Login succeeded but tokens object is missing');
      }

      if (!tokens.accessToken) {
        console.error('[AuthSlice] ERROR: tokens.accessToken is undefined or null!');
        console.error('[AuthSlice] tokens object:', tokens);
        return rejectWithValue('Login succeeded but accessToken is missing');
      }

      if (!tokens.refreshToken) {
        console.error('[AuthSlice] ERROR: tokens.refreshToken is undefined or null!');
      }

      // ========== SAVE ACCESS TOKEN ==========
      console.log('[AuthSlice] ========== SAVING TOKENS ==========');
      console.log('[AuthSlice] BEFORE localStorage.setItem - accessToken in storage:', localStorage.getItem('accessToken'));

      const accessToken = tokens.accessToken;
      const refreshToken = tokens.refreshToken;

      console.log('[AuthSlice] About to save accessToken:', accessToken.substring(0, 30) + '...');
      localStorage.setItem('accessToken', accessToken);

      console.log('[AuthSlice] AFTER localStorage.setItem - accessToken in storage:', localStorage.getItem('accessToken')?.substring(0, 30) + '...');

      // ========== SAVE REFRESH TOKEN ==========
      // Always save to localStorage for persistence (sessionStorage clears on tab close)
      if (refreshToken) {
        console.log('[AuthSlice] About to save refreshToken:', refreshToken.substring(0, 30) + '...');
        localStorage.setItem('refreshToken', refreshToken);
        console.log('[AuthSlice] AFTER localStorage.setItem - refreshToken in storage:', localStorage.getItem('refreshToken')?.substring(0, 30) + '...');
      }

      // ========== VERIFY TOKENS WERE SAVED ==========
      const verifyAccessToken = localStorage.getItem('accessToken');
      const verifyRefreshToken = localStorage.getItem('refreshToken');

      console.log('[AuthSlice] ========== VERIFICATION ==========');
      console.log('[AuthSlice] Verified accessToken exists:', !!verifyAccessToken);
      console.log('[AuthSlice] Verified refreshToken exists:', !!verifyRefreshToken);

      if (!verifyAccessToken) {
        console.error('[AuthSlice] CRITICAL: accessToken was NOT saved to localStorage!');
        return rejectWithValue('Failed to save access token to storage');
      }

      // ========== MAP USER TO ADMIN FORMAT ==========
      const admin = {
        id: String(user.id),
        email: user.email,
        name: user.firstName && user.lastName
          ? `${user.firstName} ${user.lastName}`.trim()
          : user.name || user.email,
        role: user.role || 'admin',
        permissions: user.permissions || [],
        avatar: user.avatar,
        phone: user.phone,
        createdAt: user.createdAt,
        updatedAt: user.updatedAt,
      };

      console.log('[AuthSlice] ========== SUCCESS ==========');
      console.log('[AuthSlice] Returning admin:', admin);

      return {
        admin,
        token: accessToken,
      };
    } else {
      console.log('[AuthSlice] ========== LOGIN FAILED ==========');
      console.log('[AuthSlice] response.success:', response.success);
      console.log('[AuthSlice] response.message:', response.message);
      return rejectWithValue(response.message || 'Login failed');
    }
  } catch (error: unknown) {
    console.error('[AuthSlice] ========== LOGIN ERROR ==========');
    console.error('[AuthSlice] Error:', error);
    const errorObj = error as { message?: string; status?: number };
    const errorMessage = errorObj?.message || 'An error occurred during login. Please try again.';
    return rejectWithValue(errorMessage);
  }
});

export const checkAuth = createAsyncThunk('auth/checkAuth', async (_, { rejectWithValue, getState }) => {
  // If already authenticated (e.g. just logged in), skip the API call
  const state = getState() as { auth: AuthState };
  if (state.auth.isAuthenticated && state.auth.admin) {
    console.log('[checkAuth] Already authenticated, skipping API call');
    return state.auth.admin;
  }

  try {
    const token = localStorage.getItem('accessToken');
    console.log('[checkAuth] Token in localStorage:', !!token);
    if (!token) {
      return rejectWithValue('No token found');
    }

    console.log('[checkAuth] Calling getProfile...');
    const response = await authApi.getProfile();
    console.log('[checkAuth] getProfile response:', JSON.stringify(response));
    if (response.success && response.data) {
      // Backend returns { admin: { ... } } inside data
      const adminData = (response.data as any).admin || response.data;
      console.log('[checkAuth] adminData:', JSON.stringify(adminData));
      // Map to expected Admin format
      const result = {
        id: String(adminData.id),
        email: adminData.email,
        name: adminData.firstName && adminData.lastName
          ? `${adminData.firstName} ${adminData.lastName}`.trim()
          : adminData.name || adminData.email,
        role: adminData.role || 'admin',
        permissions: adminData.permissions || [],
        avatar: adminData.avatar,
        phone: adminData.phone,
        isActive: adminData.isActive,
        createdAt: adminData.createdAt,
        updatedAt: adminData.updatedAt,
      };
      console.log('[checkAuth] Returning success:', JSON.stringify(result));
      return result;
    }
    console.log('[checkAuth] Response not successful or no data');
    return rejectWithValue(response.message || 'Failed to verify authentication');
  } catch (error: unknown) {
    console.error('[checkAuth] ERROR caught:', error);
    // Don't clear tokens here — the rejected reducer handles auth state.
    // Only clear tokens on explicit logout, not on transient network errors.
    return rejectWithValue('Session expired. Please login again.');
  }
});

export const logoutAsync = createAsyncThunk('auth/logoutAsync', async () => {
  try {
    await authApi.logout();
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    sessionStorage.removeItem('refreshToken');
    return null;
  } catch {
    // Even if logout API fails, clear local storage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    sessionStorage.removeItem('refreshToken');
    return null;
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    loginSuccess: (state, action: PayloadAction<{ admin: Admin; token: string }>) => {
      state.isAuthenticated = true;
      state.admin = action.payload.admin;
      state.token = action.payload.token;
      state.loading = false;
      state.checkingAuth = false;
      state.error = null;
      localStorage.setItem('accessToken', action.payload.token);
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.isAuthenticated = false;
      state.admin = null;
      state.token = null;
      state.loading = false;
      state.checkingAuth = false;
      state.error = action.payload;
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.admin = null;
      state.token = null;
      state.loading = false;
      state.checkingAuth = false;
      state.error = null;
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      sessionStorage.removeItem('refreshToken');
    },
    updateAdmin: (state, action: PayloadAction<Partial<Admin>>) => {
      if (state.admin) {
        state.admin = { ...state.admin, ...action.payload };
      }
    },
    updateToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
      localStorage.setItem('accessToken', action.payload);
    },
    clearError: (state) => {
      state.error = null;
    },
    setCheckingAuth: (state, action: PayloadAction<boolean>) => {
      state.checkingAuth = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // loginAsync
      .addCase(loginAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginAsync.fulfilled, (state, action) => {
        state.isAuthenticated = true;
        state.admin = action.payload.admin;
        state.token = action.payload.token;
        state.loading = false;
        state.checkingAuth = false;
        state.error = null;
      })
      .addCase(loginAsync.rejected, (state, action) => {
        state.isAuthenticated = false;
        state.admin = null;
        state.token = null;
        state.loading = false;
        state.checkingAuth = false;
        state.error = action.payload as string;
      })
      // checkAuth
      .addCase(checkAuth.pending, (state) => {
        state.checkingAuth = true;
        state.error = null;
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.isAuthenticated = true;
        state.admin = action.payload;
        state.checkingAuth = false;
        state.error = null;
      })
      .addCase(checkAuth.rejected, (state, action) => {
        // Don't clear auth if a login just succeeded — a stale checkAuth
        // rejection from before login should not nuke the new session.
        if (state.isAuthenticated && state.token) {
          console.log('[checkAuth.rejected] Ignoring stale rejection — already authenticated');
          state.checkingAuth = false;
          return;
        }
        state.isAuthenticated = false;
        state.admin = null;
        state.token = null;
        state.checkingAuth = false;
        state.error = action.payload as string;
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        sessionStorage.removeItem('refreshToken');
      })
      // logoutAsync
      .addCase(logoutAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(logoutAsync.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.admin = null;
        state.token = null;
        state.loading = false;
        state.error = null;
      })
      .addCase(logoutAsync.rejected, (state) => {
        state.isAuthenticated = false;
        state.admin = null;
        state.token = null;
        state.loading = false;
      });
  },
});

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  logout,
  updateAdmin,
  updateToken,
  clearError,
  setCheckingAuth,
} = authSlice.actions;

export default authSlice.reducer;
